/**
 * «Добавить название тех.операции» — форма ЗАВЕДЕНИЯ (AK-527 → AK-564).
 *
 * Раньше этот диалог был и созданием, и правкой: правка записи открывалась
 * модалкой, доп. поля — отдельной панелью, заявка в каталог — иконкой в строке.
 * Founder 08.08: «истории там одни и те же, а UX какой-то не одинаковый».
 * По эталону справочника культур работа с существующей записью живёт в её
 * карточке (`CompanyOpTypeSheet`), а этот диалог отвечает ровно за одно —
 * завести новое название (аналог `AddCropFromCanonDialog` у культур).
 *
 * Код 1С здесь по-прежнему НЕ спрашиваем: интеграции с 1С не существует, а
 * поле обещало, что «по этому коду операции будут сопоставляться при обмене».
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import {
  referenceOpClassLabel,
  referenceTypeLabel,
  useCreateCompanyOpType,
  useReferenceOpTypes,
} from '@/lib/reference/companyOperationTypes';

interface Props {
  open: boolean;
  onClose: () => void;
}

const NO_REF = '__none__';

export function CompanyOpTypeDialog({ open, onClose }: Props) {
  const [localName, setLocalName] = useState('');
  const [refId, setRefId] = useState<string>(NO_REF);
  const [touched, setTouched] = useState(false);

  const create = useCreateCompanyOpType();
  const { data: refTypes } = useReferenceOpTypes();

  // company_id обязателен при создании. У тенанта компания одна — берём
  // первую из /companies (тот же источник, что и AppShell).
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get<Array<{ id: string }>>('/api/v1/companies'),
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const companyId = companies?.[0]?.id ?? null;

  useEffect(() => {
    if (!open) return;
    setLocalName('');
    setRefId(NO_REF);
    setTouched(false);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refOptions = useMemo(
    () =>
      (refTypes ?? [])
        .map((t) => ({ id: t.id, label: referenceTypeLabel(t) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    [refTypes],
  );

  /** Класс работы наследуется от системной операции — это факт, не выбор. */
  const selectedClass = useMemo(
    () => (refTypes ?? []).find((t) => t.id === refId)?.op_class ?? null,
    [refTypes, refId],
  );

  const valid = localName.trim() !== '' && !!companyId;

  const submit = () => {
    setTouched(true);
    if (!valid || !companyId) return;
    create.mutate(
      {
        company_id: companyId,
        local_name: localName.trim(),
        ref_id: refId === NO_REF ? null : refId,
      },
      {
        onSuccess: () => {
          toast.success('Название добавлено');
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новое название тех.операции</DialogTitle>
          <DialogDescription>
            Как эта работа называется у вас и какой операции общего справочника
            она соответствует.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="cot-new-name">Как называется у нас</Label>
            <Input
              id="cot-new-name"
              placeholder="Например, Культивация предпосевная"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
            />
            {touched && !localName.trim() && (
              <p className="text-xs text-destructive">Укажите название.</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cot-new-ref">В системе это</Label>
            <Select value={refId} onValueChange={setRefId}>
              <SelectTrigger id="cot-new-ref">
                <SelectValue placeholder="Без привязки" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_REF}>Без привязки</SelectItem>
                {refOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {referenceOpClassLabel(selectedClass)
                ? `Класс работы: ${referenceOpClassLabel(selectedClass)?.toLowerCase()}. Привязка подставляет поля при заведении операции и позволяет сравнивать хозяйства между собой.`
                : 'Привязка подставляет поля при заведении операции и позволяет сравнивать хозяйства между собой. Можно оставить без привязки и запросить операцию в системный каталог позже.'}
            </p>
          </div>

          {create.error && (
            <Alert variant="destructive">
              <AlertDescription>{create.error.message}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Сохраняем…' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CompanyOpTypeDialog;
